import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} ProductAddonsRefs
 * @property {HTMLElement} [liveRegion]
 *
 * @extends Component<ProductAddonsRefs>
 */
class ProductAddonsComponent extends Component {
  #abortController = new AbortController();
  /** @type {Map<string, { id: number, quantity: number, properties: Record<string, string> }>} */
  #selected = new Map();

  connectedCallback() {
    super.connectedCallback();

    // Contribute selected addons when the sibling product form submits, so they
    // ride along in the same /cart/add.js call as the parent variant.
    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.cartAddCollect, this.#onCollect, {
      signal: this.#abortController.signal,
    });

    // Reset our state once the cart has confirmed the parent add succeeded.
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, {
      signal: this.#abortController.signal,
    });

    // Edit-from-cart hydration: pre-select addons listed in the URL params.
    this.#hydrateFromUrlParams();
  }

  /**
   * When the PDP loads with ?edit=true&addon_variants=<id>,<id>... (emitted by
   * cart-products.liquid's Edit kit link), pre-check those addon buttons so the
   * shopper sees what's already on their line and the re-submit preserves them.
   */
  #hydrateFromUrlParams() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('edit') !== 'true') return;
    const raw = params.get('addon_variants');
    if (!raw) return;

    const variantIds = raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    for (const variantId of variantIds) {
      const button = /** @type {HTMLButtonElement | null} */ (
        this.querySelector(`.product-addons__button[data-addon-variant-id="${CSS.escape(variantId)}"]`)
      );
      if (!button || this.#selected.has(variantId)) continue;
      const price = Number(button.dataset.addonPrice);
      this.#selected.set(variantId, {
        id: Number(variantId),
        quantity: 1,
        properties: { _finalPrice: String(price) },
      });
      button.setAttribute('aria-pressed', 'true');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Toggles an addon's selection state.
   * @param {{ target: HTMLElement }} event
   */
  handleToggle(event) {
    const button = /** @type {HTMLButtonElement | null} */ (event.target.closest('.product-addons__button'));
    if (!button) return;

    const variantId = button.dataset.addonVariantId;
    const price = Number(button.dataset.addonPrice);
    if (!variantId) return;

    if (this.#selected.has(variantId)) {
      this.#selected.delete(variantId);
      button.setAttribute('aria-pressed', 'false');
    } else {
      this.#selected.set(variantId, {
        id: Number(variantId),
        quantity: 1,
        properties: { _finalPrice: String(price) },
      });
      button.setAttribute('aria-pressed', 'true');
    }

    this.dispatchEvent(
      new CustomEvent('addons:change', {
        bubbles: true,
        detail: { count: this.#selected.size, total: this.#total() },
      }),
    );
  }

  /**
   * Called synchronously by <product-form-component> right before it submits.
   * We push each selected addon into the shared items array, tagged with the
   * parent variant id so the cart can link them back later.
   * @param {Event} event
   */
  #onCollect = (event) => {
    const detail = /** @type {{ items: Array<object>, parentVariantId: string }} */ (
      /** @type {CustomEvent} */ (event).detail
    );
    if (this.#selected.size === 0) return;
    if (
      this.dataset.productId &&
      !this.#matchesProductForm(/** @type {Event & { target: EventTarget | null }} */ (event))
    )
      return;

    const parentVariantId = detail.parentVariantId;
    for (const entry of this.#selected.values()) {
      detail.items.push({
        ...entry,
        properties: { ...entry.properties, _apa_addon_parent: parentVariantId },
      });
    }
  };

  /**
   * Reset selection after a successful parent-form add. The same form may host
   * multiple products via variant swaps, so we only react to events whose
   * productId matches ours.
   * @param {Event} event
   */
  #onCartUpdate = (event) => {
    const detail = /** @type {any} */ (event).detail;
    if (detail?.data?.didError) return;
    if (detail?.data?.source !== 'product-form-component') return;
    if (detail?.data?.productId !== this.dataset.productId) return;
    if (this.#selected.size === 0) return;

    this.#announce();
    this.#resetSelection();
  };

  /**
   * Ensures the collect event came from the product form sitting in the same
   * section as this addons block (defensive — multiple product forms could
   * theoretically exist via embeds).
   * @param {Event & { target: EventTarget | null }} event
   */
  #matchesProductForm(event) {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const productForm = target?.closest('product-form-component');
    if (!productForm) return true;
    return productForm.getAttribute('data-product-id') === this.dataset.productId;
  }

  #resetSelection() {
    this.#selected.clear();
    this.querySelectorAll('.product-addons__button[aria-pressed="true"]').forEach((btn) =>
      btn.setAttribute('aria-pressed', 'false'),
    );
  }

  #announce() {
    const { liveRegion } = this.refs;
    if (!liveRegion) return;
    liveRegion.textContent = Theme?.translations?.added || 'Added';
    setTimeout(() => {
      liveRegion.textContent = '';
    }, 4000);
  }

  /** @returns {number} Total price in cents of currently-selected addons. */
  #total() {
    let total = 0;
    for (const entry of this.#selected.values()) {
      total += Number(entry.properties._finalPrice) || 0;
    }
    return total;
  }
}

if (!customElements.get('product-addons-component')) {
  customElements.define('product-addons-component', ProductAddonsComponent);
}
